import * as THREE from 'three';
import { DefaultColors } from '../defines';
import { Viewer } from '../viewer/viewer';
import { Utils } from '../utils';
import { createDepthFadeMeshMaterial } from '../materials/DepthFadeUtils';

export class SphereMarker extends THREE.Object3D {
  viewer: Viewer;
  mesh: THREE.Mesh;
  backgroundMesh: THREE.Mesh;
  private scaleFactor = 1;
  private color = DefaultColors.TEMPORARY_OBJECTS;
  private _boundUpdateScale: () => void;

  constructor(viewer: Viewer) {
    super();
    this.viewer = viewer;

    // Create mesh with depth fade material
    const mainMaterial = createDepthFadeMeshMaterial(this.color, 1.0);
    this.mesh = new THREE.Mesh(new THREE.SphereGeometry(0.4, 10, 10), mainMaterial);

    // Create background mesh with depth fade material
    const bgMaterial = createDepthFadeMeshMaterial(this.color, 0.0);
    this.backgroundMesh = new THREE.Mesh(new THREE.SphereGeometry(0.6, 10, 10), bgMaterial);

    this.add(this.mesh);
    this.add(this.backgroundMesh);
    // Start at zero scale: the marker has no meaningful size until the first
    // updateScale (which needs a world position) — without this it renders
    // its raw world-unit geometry, huge, on the frame it first appears.
    // Owners showing a marker mid-frame call updateScale() after positioning.
    this.scale.set(0, 0, 0);
    // Wrap in a 0-arg lambda so the event-payload object isn't mistaken
    // for the optional `cameraOverride` we now accept on `updateScale`.
    this._boundUpdateScale = () => this.updateScale();
    viewer.addEventListener('update', this._boundUpdateScale);
  }

  highlight() {
    const material = this.mesh.material as THREE.ShaderMaterial;
    material.uniforms.uColor.value = this.color.clone().multiplyScalar(0.75).addScalar(0.5);
  }

  unHighlight() {
    const material = this.mesh.material as THREE.ShaderMaterial;
    material.uniforms.uColor.value = this.color;
  }

  showBorder() {
    const material = this.backgroundMesh.material as THREE.ShaderMaterial;
    material.uniforms.uOpacity.value = 0.25;
  }

  hideBorder() {
    const material = this.backgroundMesh.material as THREE.ShaderMaterial;
    material.uniforms.uOpacity.value = 0.0;
  }

  setColor(color: THREE.Color) {
    this.color = color;
    const mainMaterial = this.mesh.material as THREE.ShaderMaterial;
    const bgMaterial = this.backgroundMesh.material as THREE.ShaderMaterial;
    mainMaterial.uniforms.uColor.value = color;
    bgMaterial.uniforms.uColor.value = color;
  }

  setScaleFactor(scale: number) {
    this.scaleFactor = scale;
  }

  /**
   * Set the render order of both the marker and its border mesh. The depth-fade
   * material is `depthTest: false`, so draw order alone decides what paints on
   * top — a high value keeps the marker above meshes like the skeleton walls
   * (renderOrder ≤ 5). The background sits just under the main dot.
   */
  setRenderOrder(order: number) {
    this.renderOrder = order;
    this.backgroundMesh.renderOrder = order;
    this.mesh.renderOrder = order + 1;
  }

  /** With no args, sizes against the main view's active camera + main
   *  renderer size (default per-frame behavior wired through the
   *  'update' event). With args, called from `viewer.renderOneView`
   *  before each non-main view's render so the sphere appears the
   *  same screen-space size in widgets too. */
  updateScale(cameraOverride?: THREE.PerspectiveCamera | THREE.OrthographicCamera, widthOverride?: number, heightOverride?: number) {
    const camera = cameraOverride ?? this.viewer.scene.getActiveCamera();
    let clientWidth: number, clientHeight: number;
    if (widthOverride !== undefined && heightOverride !== undefined) {
      clientWidth = widthOverride;
      clientHeight = heightOverride;
    } else {
      const renderAreaSize = this.viewer.renderer.getSize(new THREE.Vector2());
      clientWidth = renderAreaSize.width;
      clientHeight = renderAreaSize.height;
    }

    const distance = camera.position.distanceTo(this.getWorldPosition(new THREE.Vector3()));
    const pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);

    const scale = 15 / pr;
    this.scale.set(scale, scale, scale);
    this.mesh.scale.set(this.scaleFactor, this.scaleFactor, this.scaleFactor);
  }

  dispose() {
    this.viewer.removeEventListener('update', this._boundUpdateScale);
  }
}
